import _ from "lodash";
import { ActionTree, MutationTree } from "vuex";
import SolutionsApi, { TicketClosedError } from "@/api/SolutionsApi";
import {
  ISolutionArticle,
  ISolutionArticleSearchQuery,
  ISolutionArticleSearchResult,
  ISolutionCategoryAndFolderStructure,
  ISupportRequestFilters,
  ISupportRequestInput,
  ISupportRequest,
  ISupportRequestConversationItemInput,
  ISupportRequestConversationItem,
  IndexTicket,
  IRootState,
} from "@iloq/shared";
import config from "@/config";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";
import Vue from "vue";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { makeCancellable, takeLatest } from "vuex-cancellable-actions";
import i18n from "@/i18n";

const articlePageSize: number = parseInt(config.get("solutionArticleSearchPageSize"), 10) || 30;
const ticketPageSize: number = config.get("supportTicketSearchPageSize");
const solutionsApi = new SolutionsApi();
const handleError = (error: Error) => {
  handleCommonApiErrors(error, false, true);
};

export interface ISolutionsState {
  search: {
    searchQuery: ISolutionArticleSearchQuery;
    searchResult: ISolutionArticleSearchResult;
    searching: boolean;
    loadingMore: boolean;
  };
  structure: ISolutionCategoryAndFolderStructure;
  quickSearch: {
    lastPicked: ISolutionArticle | null;
  };
  frontPage: {
    howToVideos: ISolutionArticle[];
  };
  hotTopics: ISolutionArticle[];
  supportRequests: {
    values: ISupportRequest[];
    count: number;
  } | null;
  supportConversations: {
    [key: string]: any;
  };
  conversationLoading: boolean;
  ticketsLoading: boolean;
  sendingReply: boolean;
  lastCreatedTicket:
    | {
        ticket: ISupportRequest;
        timestamp: number;
      }
    | undefined;
}

const initialState: ISolutionsState = {
  search: {
    searchQuery: {
      term: "",
      lang: "en",
      top: articlePageSize,
      skip: 0,
    },
    searchResult: {
      count: 0,
      value: [],
    },
    searching: false,
    loadingMore: false,
  },
  structure: {
    categories: [],
  },
  quickSearch: {
    lastPicked: null,
  },
  frontPage: {
    howToVideos: [],
  },
  hotTopics: [],
  supportRequests: null,
  supportConversations: {},
  conversationLoading: false,
  ticketsLoading: false,
  sendingReply: false,
  lastCreatedTicket: undefined,
};

const actions: ActionTree<ISolutionsState, IRootState> = {
  initializeState({ commit, dispatch }, languageCode: string): void {
    commit("setLanguageCode", languageCode);
    dispatch("fetchStructure")
      .then(() => {
        dispatch("searchArticles");
      })
      .catch(handleCommonApiErrors);
    dispatch("fetchHotTopics");
    dispatch("fetchFrontPageHowToVideos");
  },
  fetchStructure({ commit }): Promise<ISolutionCategoryAndFolderStructure> {
    return solutionsApi
      .fetchCategoryAndFolderStructure()
      .then((res: ISolutionCategoryAndFolderStructure) => {
        commit("setStructure", res);
        return res;
      });
  },
  fetchHotTopics({ commit, state }): void {
    const searchState = (state as ISolutionsState).search;
    const query = {
      term: "",
      lang: searchState.searchQuery.lang,
      tag: "Hot",
      sort: "updatedAt desc",
      top: 100,
      skip: 0,
    };
    solutionsApi
      .searchArticles(query)
      .then((res: ISolutionArticleSearchResult) => {
        commit("setHotTopics", res.value);
      })
      .catch(handleCommonApiErrors);
  },
  fetchFrontPageHowToVideos({ commit, state }): void {
    const searchState = (state as ISolutionsState).search;
    const query = {
      term: "",
      lang: searchState.searchQuery.lang,
      categoryId: 80000456127,
      sort: "updatedAt desc",
      top: 2,
      skip: 0,
    };
    solutionsApi
      .searchArticles(query)
      .then((res: ISolutionArticleSearchResult) => {
        commit("setFrontPageHowToVideos", res.value);
      })
      .catch(handleCommonApiErrors);
  },
  clearSearch({ commit, dispatch }): void {
    commit("setTerm", "");
    dispatch("searchArticles");
  },
  updateTerm({ commit, dispatch }, term: string): void {
    commit("setTerm", term);
    dispatch("searchArticles");
  },
  updateTermWithoutSearching({ commit }, term: string): void {
    commit("setTerm", term);
  },
  updateCategoryId({ commit, dispatch }, categoryId?: number): void {
    commit("setCategoryId", categoryId);
    dispatch("searchArticles");
  },
  updateCategoryIdWithoutSearching({ commit }, categoryId?: number): void {
    commit("setCategoryId", categoryId);
  },
  updateQueryPartially(
    { commit, dispatch },
    partialQuery: { term: string; categoryId?: number },
  ): void {
    commit("setTerm", partialQuery.term);
    commit("setCategoryId", partialQuery.categoryId);
    dispatch("searchArticles");
  },
  searchArticles({ commit, state }): void {
    commit("setSearching", true);
    commit("setLoadingMore", false);

    const searchState = (state as ISolutionsState).search;
    const query = _.cloneDeep(searchState.searchQuery);

    solutionsApi
      .searchArticles(query)
      .then((res: ISolutionArticleSearchResult) => {
        // Ignore results if query has changed during the search was running
        if (_.isEqual(query, searchState.searchQuery)) {
          commit("setSearchResult", res);
          commit("setSearching", false);
        }
      })
      .catch(handleCommonApiErrors);
  },
  loadMoreArticles({ commit, state }): void {
    const searchState = (state as ISolutionsState).search;
    const searchResult = searchState.searchResult;
    if (searchState.loadingMore || searchResult.value.length >= searchResult.count) {
      return;
    }
    commit("setLoadingMore", true);

    const originalQuery = _.cloneDeep(searchState.searchQuery);
    const query = _.cloneDeep(searchState.searchQuery);
    query.skip = searchState.searchResult.value.length;

    solutionsApi
      .searchArticles(query)
      .then((res: ISolutionArticleSearchResult) => {
        // Ignore results if query has changed during the load was running
        if (_.isEqual(originalQuery, searchState.searchQuery)) {
          commit("addSearchResults", res.value);
          commit("setLoadingMore", false);
        }
      })
      .catch(handleCommonApiErrors);
  },
  searchArticlesQuick(
    { state },
    searchQuery: ISolutionArticleSearchQuery,
  ): Promise<ISolutionArticleSearchResult | void> {
    const searchState = (state as ISolutionsState).search;
    searchQuery.lang = searchState.searchQuery.lang;
    return solutionsApi.searchArticles(searchQuery).catch(handleCommonApiErrors);
  },

  async createSupportTicket(
    { commit },
    ticket: ISupportRequestInput,
  ): Promise<IndexTicket | undefined> {
    let result: IndexTicket | undefined;
    try {
      result = await solutionsApi.createSupportTicket(ticket);
      commit("setLastCreatedTicket", result);
    } catch (error) {
      handleCommonApiErrors(error as Error);
      return undefined;
    }
    // Note: no need to fetch listing here, it's handled by beforeMount of SupportRequestList component when arriving there
    return result;
  },

  ...makeCancellable({
    fetchSupportRequests: takeLatest(
      async ({ commit, rootState, state }, params: ISupportRequestFilters): Promise<void> => {
        const { partnerId } = rootState.user ?? {};
        if (partnerId) {
          commit("setTicketsLoading", true);

          const supportRequests = await solutionsApi
            .searchSupportTickets({
              ...params,
              partnerId,
            })
            .catch(handleError);

          const isDefaultQuery =
            Number(params.pageState.top) === ticketPageSize &&
            Number(params.pageState.skip) === 0 &&
            !params.term;
          const ticketCreatedLatelyAndMightBeMissingFromSearchIndexResultsBecauseOfIndexingDelays =
            state.lastCreatedTicket && Date.now() - state.lastCreatedTicket.timestamp < 10000;
          if (
            isDefaultQuery &&
            ticketCreatedLatelyAndMightBeMissingFromSearchIndexResultsBecauseOfIndexingDelays
          ) {
            const shouldAppend =
              supportRequests!.values.find(
                (request) => request.id === state.lastCreatedTicket.id,
              ) === undefined;
            if (shouldAppend) {
              // This "craps" the paging, but it's less bad than not showing the just-created ticket at all
              supportRequests!.values.unshift(state.lastCreatedTicket.ticket);
            }
          }

          commit("setSupportRequests", {
            supportRequests,
            pageState: params.pageState,
          });
          commit("setTicketsLoading", false);
        }
      },
    ),
  }),

  async fetchConversation(
    { commit },
    { ticketId, afterDate, partnerId }: { ticketId: string; afterDate: string; partnerId: string },
  ): Promise<ISupportRequestConversationItem[] | null> {
    if (ticketId && afterDate && partnerId) {
      const conversation = await solutionsApi
        .fetchConversation({ ticketId, afterDate, partnerId })
        .catch(handleError);

      if (conversation) return conversation;
    }
    return null;
  },

  async submitReply(
    { commit },
    reply: ISupportRequestConversationItemInput,
  ): Promise<IndexTicket | undefined> {
    commit("sendingReply", true);
    try {
      const updatedTicket = await solutionsApi.submitReply(reply);
      commit("sendingReply", false);
      return updatedTicket;
    } catch (error) {
      commit("sendingReply", false);
      if (error instanceof TicketClosedError) {
        commit(
          "showGeneralError",
          {
            dialogTitle: i18n.t("tickets.ticketClosedCannotReply.title"),
            dialogText: i18n.t("tickets.ticketClosedCannotReply.body"),
          },
          {
            root: true,
          },
        );
        return;
      }
      handleCommonApiErrors(error as Error);
    }
  },

  async fetchTicketAttachmentURL(
    { commit },
    { ticketId, attachmentId }: { ticketId: string; attachmentId: string },
  ): Promise<string | null> {
    const attachmentURL = await solutionsApi
      .fetchTicketAttachmentURL({ ticketId, attachmentId })
      .catch(handleError);
    if (attachmentURL) return attachmentURL;
    return null;
  },

  async fetchConversationAttachmentURL(
    { commit },
    {
      ticketId,
      messageId,
      attachmentId,
    }: { ticketId: string; messageId: string; attachmentId: string },
  ): Promise<string | null> {
    const attachmentURL = await solutionsApi
      .fetchConversationAttachmentURL({ ticketId, messageId, attachmentId })
      .catch(handleError);
    if (attachmentURL) return attachmentURL;
    return null;
  },

  async fetchSolutionAttachmentURL(
    { commit },
    { article, attachmentId }: { article: ISolutionArticle; attachmentId: string },
  ): Promise<string | null> {
    // id format is "<freshdesk ID>-<language code>", e.g. "12345678-fi"
    const [articleId, languageCode] = article.id.split("-");
    const attachmentURL = await solutionsApi
      .fetchSolutionAttachmentURL({ articleId, languageCode, attachmentId })
      .catch(handleCommonApiErrors);
    if (attachmentURL) return attachmentURL;
    return null;
  },
};

const mutations: MutationTree<ISolutionsState> = {
  setSearching(state: ISolutionsState, searching: boolean): void {
    state.search.searching = searching;
  },
  setLoadingMore(state: ISolutionsState, loadingMore: boolean): void {
    state.search.loadingMore = loadingMore;
  },
  setTerm(state: ISolutionsState, term: string): void {
    state.search.searchQuery.term = term;
  },
  setCategoryId(state: ISolutionsState, categoryId?: number): void {
    state.search.searchQuery.categoryId = categoryId;
  },
  setLanguageCode(state: ISolutionsState, languageCode: string): void {
    state.search.searchQuery.lang = languageCode;
  },
  setSearchResult(state: ISolutionsState, searchResult: ISolutionArticleSearchResult): void {
    state.search.searchResult = searchResult;
  },
  addSearchResults(state: ISolutionsState, value: ISolutionArticle[]): void {
    state.search.searchResult.value.push(...value);
  },
  setStructure(state: ISolutionsState, structure: ISolutionCategoryAndFolderStructure): void {
    state.structure = structure;
  },
  setHotTopics(state: ISolutionsState, hotTopics: ISolutionArticle[]): void {
    state.hotTopics = hotTopics;
  },
  setFrontPageHowToVideos(state: ISolutionsState, howToVideos: ISolutionArticle[]): void {
    state.frontPage.howToVideos = howToVideos;
  },
  setLastPicked(state: ISolutionsState, article: ISolutionArticle): void {
    state.quickSearch.lastPicked = article;
  },
  setSupportRequests(
    state: ISolutionsState,
    {
      supportRequests,
      pageState,
    }: {
      supportRequests: {
        values: ISupportRequest[];
        count: number;
      };
      pageState: {
        top: number;
        skip: number;
      };
    },
  ): void {
    // If paging options are set to defaults we know we should replace support requests and NOT concatenate to previous results.
    const isDefault = Number(pageState.top) === ticketPageSize && Number(pageState.skip) === 0;

    Vue.set(state, "supportRequests", {
      ...state.supportRequests,
      values: isDefault
        ? supportRequests?.values
        : (state.supportRequests?.values.concat(supportRequests.values) ?? supportRequests.values),
      count: supportRequests?.count ?? state.supportRequests?.count,
    });
  },
  setConversationLoading(state: ISolutionsState, loading: boolean): void {
    state.conversationLoading = loading;
  },
  setTicketsLoading(state: ISolutionsState, loading: boolean): void {
    state.ticketsLoading = loading;
  },
  sendingReply(state: ISolutionsState, loading: boolean): void {
    state.sendingReply = loading;
  },
  setLastCreatedTicket(state: ISolutionsState, ticket: ISupportRequest): void {
    state.lastCreatedTicket = {
      ticket,
      timestamp: Date.now(),
    };
  },
};

export const solutionsModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};
